import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import "../assets/css/InfoTip.css";

/**
 * "Info" pill (small oval link) after a title or label; click shows the help text, Escape or a click
 * outside closes it. Use instead of an (i) icon with a hover title, which touch screens cannot open.
 */
function InfoTip({ children, text, label = "More info", className = "" }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !popRef.current) return;
    const rect = popRef.current.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) setAlignRight(true);
  }, [open]);

  const content = children ?? text;
  if (!content) return null;
  return (
    <span className={`co-info ${className}`.trim()} ref={wrapRef}>
      <button
        type="button"
        className="co-info__pill"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAlignRight(false);
          setOpen((v) => !v);
        }}
      >
        Info
      </button>
      {open ? (
        <span id={popId} ref={popRef} role="note" className={`co-info__pop${alignRight ? " is-right" : ""}`}>
          {content}
        </span>
      ) : null}
    </span>
  );
}

export default InfoTip;
